import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { CultivosContextService } from '../../context/cultivos-context.service';
import { CultivosService } from '../../services/cultivos.service';
import { Router } from '@angular/router';
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: 'app-productores',
  templateUrl: './productores.component.html'
})
export class ProductoresComponent implements OnInit, OnDestroy {

  private readonly _DEFAULT_ITEMS_PER_PAGE = 10;
  private _filters;
  private _filtersDropDown;
  public filtersVisible = false;
  public filtersForm: UntypedFormGroup;
  public dropdownInfo: number;
  public dropdownSection: number;
  public productoresList;
  public mensajes = new Array<Message>();
  public showError: boolean;
  public showWarning: boolean;
  public newEditModal = false;
  public deleteModal = false;
  public exportModal = false;
  public exportFormat = '';
  public pestanaActiva = new Array<number>();

  public denominaciones;
  public provincias;
  public municipios;

  public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
  public currentPage = 0; // La paginación en back empieza por 0, como los arrays.
  public totalItems: number;
  public readonly visiblePages = 3;

  public productorId;
  public disabledDenominaciones: Subject<boolean> = new Subject();
  public disabledProvincia: Subject<boolean> = new Subject();
  public disabledMunicipio: Subject<boolean> = new Subject();

  public orderBy: string;
  public columnId: number;

  public toCloseSpinner = 0;

  public csvImportModal = false;
  public csvStep = 'initial';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly _router: Router,
    private _fb: UntypedFormBuilder,
    private readonly _sessionContext: SessionContextService,
    private readonly _spinnerService: SpinnerService,
    private readonly _cultivosService: CultivosService,
    private readonly _cultivosContext: CultivosContextService,
    private readonly _dataProvider: DataProvider,
    private readonly utilsService: UtilsService,
    private readonly _translate: TranslateService) { }

  ngOnInit() {
    this.filtersForm = this._fb.group({
      productor: [''],
      pais: [''],
      denominacion: [{ value: '', disabled: true }],
      paisProductor: [''],
      provincia: [{ value: '', disabled: true }],
      municipio: [{ value: '', disabled: true }],
      finca: [''],
      licencia: [''],
      fechaInicio: [''],
      fechaFin: [''],
      dateType: ['false']
    });
    this._cultivosContext.productoresFilters = null;

    this.checkWarnings(this.filtersForm.get('pais').value);

    this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
    this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

    this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
      if (pais) {
        this._filtersDropDown = {
          paisId: pais.id
        };

        this._spinnerService.showSpinner();
        this._cultivosService.getDenominaciones(this._filtersDropDown).toPromise().finally(() => {
          this._spinnerService.hideSpinner();
        }).then((resp) => {
          if (resp.lista.length > 0) {
            this.denominaciones = resp.lista;
            if (this.denominaciones.length === 1) {
              this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
              this.filtersForm.get('denominacion').disable();
              this.disabledDenominaciones.next(true);
            } else {
                this.filtersForm.get('denominacion').enable();
                this.disabledDenominaciones.next(false);
            }
          }
        });
      } else {
        this.denominaciones = null;
        this.disabledDenominaciones.next(true);
        this.filtersForm.get('denominacion').reset();
      }
    });

    this.filtersForm.get('paisProductor').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
      if (pais) {
        this._filtersDropDown = {
          paisId: pais.id
        };

        this._spinnerService.showSpinner();
        this._cultivosService.getProvinciasPais(this._filtersDropDown).toPromise().finally(() => {
          this._spinnerService.hideSpinner();
        }).then((resp) => {
          if (resp.lista.length > 0) {
            this.provincias = resp.lista;
            this.disabledProvincia.next(false);
          }
        });
      } else {
        this.provincias = null;
        this.municipios = null;
        this.disabledProvincia.next(true);
        this.disabledMunicipio.next(true);
        this.filtersForm.get('municipio').reset();
        this.filtersForm.get('provincia').reset();
      }
    });

    this.filtersForm.get('provincia').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((provincia) => {
      if (provincia) {
        this._filtersDropDown = {
          provinciaId: provincia.id
        };
        this._spinnerService.showSpinner();
        this._cultivosService.getMunicipiosProvincia(this._filtersDropDown).toPromise().finally(() => {
          this._spinnerService.hideSpinner();
        }).then((resp) => {
          if (resp.lista.length > 0) {
            this.municipios = resp.lista;
            this.disabledMunicipio.next(false);
          }
        });
      } else {
        this.municipios = null;
        this.disabledMunicipio.next(true);
        this.filtersForm.get('municipio').reset();
      }
    });


  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkWarnings(pais) {
    if (this.filtersForm.valid) {
      this.filtersVisible = false;
      this.showWarning = false;
      this.mensajes = [];
      this.getProductores();
    } else {
      this.filtersVisible = true;
      this.showWarning = true;
      this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
    }
  }

  public get paises() {
    if (this._dataProvider.paises) {
        if (this._dataProvider.paises === 1 && this.filtersForm.get('pais').enabled) {
          this.filtersForm.get('pais').patchValue(this._dataProvider.paises[0]);
          this.filtersForm.get('pais').disable();
        }
      }
    return this._dataProvider.paises;
  }

  public get role() {
    return this._sessionContext.sessionUser.role;
  }

  public get claims() {
    return this._sessionContext.sessionUser.lsClaimTypes;
  }

  public get isSmallScreen(){
    return this._sessionContext.isSmallScreen();
  }

  public pageChange(page) {
    this.dropdownInfo = -1;
    this.dropdownSection = -1;
    this._cultivosContext.productoresFilters.numPagina = this.currentPage = page - 1;
    this.getProductores();
  }

  public itemsPerPageChange(items) {
    this.dropdownInfo = -1;
    this.dropdownSection = -1;
    this._cultivosContext.productoresFilters.numPagina = this.currentPage = 0;
    this._cultivosContext.productoresFilters.tamPagina = this.itemsPerPage = items;
    this.getProductores();
  }

  private clearDatesValidators() {
    if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').value) {
      this.filtersForm.get('fechaFin').clearValidators();
      this.filtersForm.get('fechaFin').updateValueAndValidity();
    }
    if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').value) {
      this.filtersForm.get('fechaInicio').clearValidators();
      this.filtersForm.get('fechaInicio').updateValueAndValidity();
    }
  }

  public validDates() {
    if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
      if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
        this.clearDatesValidators();
      } else {
        this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
        this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
      }
      if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
        this.showError = true;
        this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
      } else {
        this.showError = false;
        this.mensajes = [];
      }
    } else {
      this.clearDatesValidators();
      this.showError = false;
      this.mensajes = [];
    }
  }

  private checkDates(inicio, fin) {
    const inicioD = new Date(inicio);
    const finD = new Date(fin);
    return inicioD <= finD;
  }

  public checkValuesForm(): boolean {
    return (this.filtersForm.get('productor').value ||
      (this.filtersForm.get('denominacion').value && this.filtersForm.get('denominacion').enabled) ||
      (this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
      this.filtersForm.get('paisProductor').value ||
      this.filtersForm.get('provincia').value ||
      this.filtersForm.get('municipio').value ||
      this.filtersForm.get('finca').value ||
      this.filtersForm.get('licencia').value ||
      this.filtersForm.get('fechaInicio').value ||
      this.filtersForm.get('fechaFin').value) ? true : false;
  }

  public resetForm() {
    this.filtersForm.reset({
      pais: this.filtersForm.get('pais').disabled ? this.filtersForm.get('pais').value : null,
      denominacion: this.filtersForm.get('denominacion').disabled ? this.filtersForm.get('denominacion').value : null,
      dateType: 'false'
    });
    this._filtersDropDown = null;
  }

  public filtrar() {
    this.validDates();

    if (this.filtersForm.valid) {
      this.applyFilters();
    }
  }

  private applyFilters() {
    this.currentPage = 0;
    this._cultivosContext.productoresFilters = {
      paisIdAcceso: this.filtersForm.get('pais').value ? this.filtersForm.get('pais').value.id : null,
      denominacionIdAcceso: this.filtersForm.get('denominacion').value ? this.filtersForm.get('denominacion').value.id : null,
      paisIdProductor: this.filtersForm.get('paisProductor').value ? this.filtersForm.get('paisProductor').value.id : null,
      provinciaIdProductor: this.filtersForm.get('provincia').value ? this.filtersForm.get('provincia').value.id : null,
      municipioIdProductor: this.filtersForm.get('municipio').value ? this.filtersForm.get('municipio').value.id : null,
      desProductor: this.filtersForm.get('productor').value ? this.filtersForm.get('productor').value : '',
      desFinca: this.filtersForm.get('finca').value ? this.filtersForm.get('finca').value : '',
      nLicencia: this.filtersForm.get('licencia').value ? this.filtersForm.get('licencia').value : '',
      cargaRegistroenUso: true,
      fechaInicio: this.filtersForm.get('fechaInicio').value ?
        this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
      fechaFin: this.filtersForm.get('fechaFin').value ?
        this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
      fechaActualizacion: this.filtersForm.get('dateType').value,
      numPagina: this.currentPage,
      tamPagina: this.itemsPerPage
    };
    this.getProductores();
  }

  public getProductores() {
    if (this._cultivosContext.productoresFilters) {
      this._filters = this._cultivosContext.productoresFilters;
    } else {
      this.applyFilters();
    }
    this._spinnerService.showSpinner();
    this._cultivosService.getProductores(this._filters).toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then((resp) => {
      this.totalItems = resp.paginacionMeta.totalCount;
      this.productoresList = resp.lista;
    }, (error) => {
      this.pushMessage('global.error.noResponse');
    });
  }

  public create() {
    this.productorId = null;
    this.newEditModal = true;
  }

  public update(productor) {
    this.productorId = productor.id;
    this.newEditModal = true;
  }

  public delete() {
    this._spinnerService.showSpinner();
    this._cultivosService.deleteProductor(this.productorId).toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then(() => {
      this.close();
      this.pushMessage('global.success.itemDeleted');
      this.getProductores();
    });
  }

  public askDelete(productor) {
    this.productorId = productor.id;
    this.deleteModal = true;
  }

  public close() {
    this.deleteModal = false;
    this.newEditModal = false;
  }

  public pushMessage(mensaje) {
    this.showError = true;
    if (mensaje.includes('error') || mensaje.includes('ApiErrors')) {
      this.mensajes.push({ class: 'error', message: mensaje });
    } else {
      this.mensajes.push({ class: 'success', message: mensaje });
    }
  }

  public refresh(value) {
    this.newEditModal = false;
    if (value) {
      this.getProductores();
    }
  }

  public showDetails(i: number) {
    if (this.role !== 'Auditor') {
      this.dropdownInfo = this.dropdownInfo === i ? -1 : i;
      // Desplazamiento al principio del panel "Más información" para tablet (mismo tamaño máx. que en table.scss)
      if (this.dropdownInfo !== -1 && window.innerWidth <= 1199) {
        // Retardo: en caso de que haya otro detalle abierto
        setTimeout(() => {
          document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
        }, 50);
      }
    }
  }

  public showSection(i) {
    this.dropdownSection = this.dropdownSection === i ? -1 : i;
    this.pestanaActiva[i] = this.pestanaActiva[i] > 0 ? this.pestanaActiva[i] : 1;
    if (this.dropdownSection !== -1 && window.innerWidth <= 1199) {
      setTimeout(() => {
        document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
      }, 50);
    }
  }

  public showHuertos(i) {
    this.pestanaActiva[i] = 1;
  }

  public showDenominaciones(i) {
    this.pestanaActiva[i] = 2;
  }

  public getRolePermission() {
    let disabled = false;

    if (this.role === 'Administrador') {
      disabled = false;
    } else if (this.role === 'Auditor') {
      disabled = false;
    } else if (this.role === 'Supervisor') {
      disabled = true;
    }

    return disabled;
  }

  public getIcon(prop: string, idColumn: number) {
    let iconClass = 'fa fa-sort';
    if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty.indexOf(prop) != -1 && this.columnId === idColumn) {
        iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
    }
    return iconClass;
  }

  public toOrderBy(columnFilter, idColumn) {
    this.columnId = idColumn;
    if (this.productoresList && this.productoresList.length > 0) {
      if (this._cultivosContext.productoresFilters) {
        this._filters = this._cultivosContext.productoresFilters;
        if (!this.orderBy || this.orderBy.includes('DESC')) {
          this.orderBy = 'ASC' + idColumn;
          this._filters.orderItems = [
            {
              orderProperty: columnFilter,
              orderByDescending: false
            }
          ];
        } else if (this.orderBy.includes('ASC')) {
          this.orderBy = 'DESC' + idColumn;
          this._filters.orderItems = [
            {
              orderProperty: columnFilter,
              orderByDescending: true
            }
          ];
        }
        this.getProductores();
      }
    }
  }

  private checkCloseSpinner() {
    this.toCloseSpinner -= 1;
    if (this.toCloseSpinner === 0) {
        this._spinnerService.hideSpinner();
    }
  }

  public openDocHelp() {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._cultivosService.getDocHelp('Crops_Producers.pdf').toPromise().finally(() => {
        this.checkCloseSpinner();
    }).then((resp) => {
        localStorage.setItem('base64', resp.base64Archivo);
        const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
        window.open(url, '_blank');
    });
  }

  public showExportModal(file) {
    this.exportModal = true;
    this.exportFormat = file;
  }

  private downloadPdf() {
    this._cultivosService.getProductoresReport(this._filters).toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then(
      (resp) => {
      const downloadLink = document.createElement('a');
      downloadLink.href = 'data:application/pdf;base64,' + resp.base64Archivo;
      downloadLink.download = resp.nombreArchivo;
      downloadLink.click();
      }, (error) => {
        this.showError = true;
        this.mensajes.push({ class: 'error', message: 'global.error.file' });
      }
    );
  }

  private downloadCsv() {
    this._cultivosService.getProductoresCsvReport(this._filters).toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then(
      (resp) => {
      const downloadLink = document.createElement('a');
      downloadLink.href = 'data:text/csv;base64,' + resp.base64Archivo;
      downloadLink.download = resp.nombreArchivo;
      downloadLink.click();
      }, (error) => {
        this.showError = true;
        this.mensajes.push({ class: 'error', message: 'global.error.file' });
      }
    );
  }

  public exportPdfOrCsv(generalOrDetailed: string) {
    if (this.productoresList && this.productoresList.length > 0) {
      if (this._cultivosContext.productoresFilters) {
        this._filters = this._cultivosContext.productoresFilters;
        this._filters.tamPagina = 0;
        this._filters.tipoArchivo = this.exportFormat === 'pdf' ? 'Pdf' : 'Csv';
        this._filters.mostrarDetalle = generalOrDetailed === 'detailed';
      }

      this.exportModal = false;
      this._spinnerService.showSpinner();

      if (this.exportFormat !== 'csv') {
        this.downloadPdf();
      } else {
        this.downloadCsv();
      }
    } else {
      this.showError = true;
      this.mensajes.push({ class: 'error', message: 'global.error.exportNoData' })
    }
  }

  public showCsvImportModal() {
    this.csvImportModal = true;
  }

  public hideCsvModal(value: string) {
    this.csvImportModal = false;

    switch (value) {
      case 'open-form':
        this.productorId = null;
        this.newEditModal = true;
        break;
      case 'refresh-page':
        this.getProductores();
        break;
    }
  }
  
  public getCsvModalClass() {
      return `csv-${this.csvStep}-parent`;
  }

  public trackByProductorId(index: number, productor: { id: number }): number {
    return productor.id;
  }
}